import {
  calculateExpression,
  setExpressionFunctionDataSync,
  TableValue,
} from '@lmt/expressions';
import {
  ConditionType,
  DataBlockTableColumnDto,
  MergeFieldInterpretation,
  parseDateOnly,
  QueryFactory,
} from '@lmt/shared-types';
import {
  DataType,
  DocumentQueryDto,
  DataBlockTypeDto,
  DocumentQueryFieldDto,
  MergeFieldDto,
  MergeFieldQueryDto,
} from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { getFromCacheOrRequest } from '@lmt/unicorn-shared/app/api/custom-hooks';
import {
  getDataBlockTypes,
  getMergeFields,
} from '@lmt/unicorn-shared/app/components/documents/expressions/data-cache';
import {
  extractMergeFieldPrefixAndCodeFromVariableName,
  extractUniqueQueriesFromDocument,
  getMergeFieldByVariableName,
} from '@lmt/unicorn-shared/app/components/documents/expressions/merge-fields-helper';

import {
  createTableWithCells,
  createTableWithDataAsColumns,
  createTableWithDataAsRows,
  mergeTablesFromDocumentVariables,
} from '@lmt/unicorn-shared/app/components/documents/richedit/table-helper';
import {
  GlobalFormsContext,
  useGlobalForms,
} from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useEffect } from 'react';
import { useEventCallback } from '@mui/material';
import { useTranslation, UseTranslationResponse } from 'react-i18next';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { isQueryIdentifiersEqual } from '../types';
import {
  CalculateDocumentVariableAsyncEventArgs,
  CharacterPropertiesApi,
  CustomCommandExecutedEventArgs,
  DocumentVariableData,
  DXR,
  FieldApi,
  IntervalApi,
  RichEdit,
  SubDocumentApi,
  SubDocumentBase,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import i18next from 'i18next';
import { NS_DataBlockFields } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { addMilliseconds, format, startOfToday } from 'date-fns';
import { ExpressionFunctionsDto } from '@lmt/expressions/src/expressions/types';

export const showFieldCodesButtonId = 'showFieldCodesButtonId';
export const showFieldValuesButtonId = 'showFieldValuesButtonId';

const missingMergeFieldStyle: Partial<CharacterPropertiesApi> = {
  bold: true,
  foreColor: 'red',
  fontName: 'Arial',
  size: 24,
};

/*
  Returns all DOCVARIABLEs that are used in a passed document
 */
function getAllDocumentVariables(richEdit: RichEdit): string[] {
  const documentVariables: string[] = [];
  forEachField(richEdit, (_subDocument, _field, variableName) => {
    documentVariables.push(variableName);
  });

  return documentVariables;
}

/*
 Executed when Merge Field was deleted, but it's still present in a document
 */
function createMissingFieldText(richEdit: RichEdit, variableName: string) {
  const documentProcessor = richEdit.createDocumentProcessor();
  documentProcessor.document.insertText(0, variableName);

  styleMissingCodeField(
    documentProcessor.document.subDocuments.getByIndex(0)!,
    new DXR.Interval(0, variableName.length),
  );
  return documentProcessor;
}

function forEachField(
  richEdit: RichEdit,
  processFunction: (
    subDocument: SubDocumentBase,
    field: FieldApi,
    variableName: string,
  ) => void,
) {
  richEdit.document.subDocuments.forEach(function (subDocument) {
    for (
      let fieldIndex = 0, field;
      (field = subDocument.fields.getByIndex(fieldIndex));
      fieldIndex++
    ) {
      if (field.name == DXR.FieldName.DocVariable) {
        // text is like {DOCVARIABLE X0001}
        const codeText = subDocument.getText(field.codeInterval);
        const result = codeText.match(/.*?(docvariable)\s+(.+)/i);
        if (result && result[2]) {
          const variableName = result[2];
          processFunction(subDocument, field, variableName);
        }
        // } else if (field.name == 0) {
        //   // text is like {X0001}
        //   const codeText = subDocument.getText(field.codeInterval);
        //   processFunction(subDocument, field, codeText);
      }
    }
  });
}

function styleMissingCodeField(
  subDocument: SubDocumentApi,
  codeInterval: IntervalApi,
) {
  subDocument.setCharacterProperties(
    codeInterval,
    missingMergeFieldStyle as any,
  );
}

export function showFieldCodes(
  richEdit: RichEdit,
  mergeFields: MergeFieldDto[],
) {
  richEdit.document.fields.showAllFieldCodes();
  replaceNonExistingFields(richEdit, mergeFields);
}

function replaceNonExistingFields(
  richEdit: RichEdit,
  mergeFields: MergeFieldDto[],
) {
  richEdit.loadingPanel.show();
  richEdit.beginUpdate();
  try {
    forEachField(richEdit, (subDocument, field, variableName) => {
      const matchResult = variableName.match(/^(?<prefix>.*?)(?<code>\d+)$/);
      if (!matchResult) return;

      const code = parseInt(matchResult?.groups?.code ?? '');
      const prefix = matchResult?.groups?.prefix;
      const mergeField = mergeFields.find(
        (x) => x.code === code && x.prefix === prefix,
      );
      if (!mergeField) {
        styleMissingCodeField(
          subDocument,
          new DXR.Interval(
            field.codeInterval.start - 1,
            field.codeInterval.length + 2,
          ),
        );
      }
    });
  } finally {
    richEdit.loadingPanel.hide();
    richEdit.endUpdate();
  }
}

function postProcessAllDataBlockFields(
  pathPrefix: string,
  data: Record<string, any>,
  currentBaseQuery: DocumentQueryDto,
  postProcessFuncs: ((
    queryField: DocumentQueryFieldDto,
    data: Record<string, any>,
    key: string,
  ) => void)[],
) {
  Object.keys(data).forEach((key) => {
    const value = data[key];

    const fullPath = key; //keyToQueryDataField(pathPrefix, key);
    const queryField = currentBaseQuery.queryFields.find(
      (x) => x.dataField === fullPath,
    );
    if (queryField) {
      postProcessFuncs.forEach((x) => x(queryField, data, key));
    } else if (value instanceof Object && !Array.isArray(value)) {
      // Nested object. We need to handle dictionaries inside it as well.
      // Most common nested object is the `custom fields` ('data' property in an example below).
      // E.g. for Visits we have the following object as data:
      // {
      //   begin: "2021-06-04T03:26:22Z"
      //   data: {txt: "dgfhdfghdfgh", checkbox: null}
      //   deviceModel: null
      //   end: "2021-06-04T03:26:24Z"
      // }
      postProcessAllDataBlockFields(
        fullPath,
        value,
        currentBaseQuery,
        postProcessFuncs,
      );
    }
  });
}

/*
Triggers updating all MergeFields and substitutes them with real values in a document
 */
export async function updateFieldsAsync(
  i18nObject: UseTranslationResponse<'translation', undefined>,
  richEdit: RichEdit,
  queryClient: QueryClient,
  globalForms: GlobalFormsContext,
  params: {
    patientId: number;
    caseId: number | null;
    visitId: number | null;
    dataBlockId: number | null;
  },
) {
  richEdit.loadingPanel.show();
  richEdit.beginUpdate();

  const docVariables = getAllDocumentVariables(richEdit);

  const uniqueQueries = await extractUniqueQueriesFromDocument(
    queryClient,
    docVariables,
    async () => {
      if (!params.caseId) return [];
      return await globalForms.openSelectVisits(params.caseId);
    },
  );
  const baseQueries = await getFromCacheOrRequest(
    queryClient,
    QueryFactory.MergeFieldQuery.getQueriesQueryKey(),
    () => QueryFactory.MergeFieldClient.getQueries(),
  );

  const dataBlockTypes = await getDataBlockTypes(queryClient);
  const dataBlockTypesDictionary = dataBlockTypes.reduce((result, item) => {
    result[item.value] = item;
    return result;
  }, {} as Record<string, DataBlockTypeDto>);
  const expressionFunctionsData: ExpressionFunctionsDto[] = [];

  //send requests for all variables to the server
  const promises = uniqueQueries.map(async (query) => {
    try {
      const queryIdentifier: MergeFieldQueryDto = {
        queryType: query.query.queryType,
        dataBlockTypeId: query.query.dataBlockTypeName
          ? dataBlockTypesDictionary[query.query.dataBlockTypeName]?.id
          : null,
        selectedVisits: query.query.selectedVisits,
      };
      const data =
        (await ClientFactory.MergeFieldClient.executeQuery({
          query: queryIdentifier,
          filter: null,
          limit: query.limit,
          secondArgument: query.expressionFunctionArgument2,
          ...params,
        })) ?? {};

      const currentBaseQuery = baseQueries!.find((x) =>
        isQueryIdentifiersEqual(x.queryIdentifier, queryIdentifier),
      )!;
      expressionFunctionsData.push({
        data: data,
        query: query,
        currentBaseQuery: currentBaseQuery,
      });
    } catch (e) {
      console.error('ShowCodesAndValuesSwitcher.updateFieldsAsync', e);
    }
  });
  await Promise.all(promises);
  const expressionFunctionsKey = setExpressionFunctionDataSync(
    expressionFunctionsData,
  );
  const mergeFields = await getMergeFields(queryClient);

  async function calculateDocumentVariable(
    richEdit: RichEdit,
    e: DocumentVariableData,
  ) {
    // If variableName contains spaces, it will be splitted into `variableName` and `args`.
    // So we need to join them, since spaces could happen
    const variableName = [e.variableName, ...e.args].join(' ');
    const mergeFieldId =
      extractMergeFieldPrefixAndCodeFromVariableName(variableName);

    let expression = variableName;
    let mergeField = null;
    if (mergeFieldId) {
      mergeField = getMergeFieldByVariableName(mergeFields, variableName);
      if (mergeField === null) {
        e.callback(createMissingFieldText(richEdit, variableName));
        console.error(`Field ${variableName} wasn't found`);
        return;
      } else {
        expression = mergeField.expression;
      }
    }
    try {
      let result =
        (await calculateExpression(expression, expressionFunctionsKey)) ?? '';
      if (
        mergeField &&
        mergeField.isInterpretationsActive &&
        !isNaN(result) &&
        !isNaN(parseFloat(result))
      ) {
        result = getInterpretedValue(mergeField, parseFloat(result));
      }
      const postProcessedResult = postProcessEvaluationResult(richEdit, result);
      e.callback(postProcessedResult);
    } catch (err) {
      e.callback(createMissingFieldText(richEdit, variableName));
      console.error(
        `Error calculating value of ${variableName}`,
        expression,
        err,
      );
      return;
    }
  }

  function getInterpretedValue(mergeField: MergeFieldDto, result: number) {
    if (mergeField.interpretations) {
      for (const interpretation of mergeField.interpretations) {
        var isMatch = isIntepretationMatch(interpretation, result);
        if (isMatch) {
          return interpretation.entry;
        }
      }
    }
    return mergeField.defaultInterpretationEntry;
  }

  function isIntepretationMatch(
    interpretation: MergeFieldInterpretation,
    result: number,
  ) {
    switch (interpretation.condition) {
      case ConditionType.Equal:
        return result == interpretation.value!;
      case ConditionType.Greater:
        return result > interpretation.value!;
      case ConditionType.GreaterOrEqual:
        return result >= interpretation.value!;
      case ConditionType.Less:
        return result < interpretation.value!;
      case ConditionType.LessOrEqual:
        return result <= interpretation.value!;
      case ConditionType.NotEqual:
        return result != interpretation.value!;
      case ConditionType.GreaterOrEqualAndLess:
        return (
          result >= interpretation.minValue! &&
          result < interpretation.maxValue!
        );
      case ConditionType.GreaterOrEqualAndLessOrEqual:
        return (
          result >= interpretation.minValue! &&
          result <= interpretation.maxValue!
        );
      case ConditionType.GreaterAndLessOrEqual:
        return (
          result > interpretation.minValue! &&
          result <= interpretation.maxValue!
        );
      case ConditionType.GreaterAndLess:
        return (
          result > interpretation.minValue! && result < interpretation.maxValue!
        );
      default:
        return false;
    }
  }

  async function calculateDocumentVariableAsyncHandler(
    richEdit: RichEdit | undefined,
    e: CalculateDocumentVariableAsyncEventArgs | undefined,
  ) {
    if (!richEdit || !e) return;
    for (const docVariable of e.data) {
      await calculateDocumentVariable(richEdit, docVariable);
    }
  }
  richEdit.events.calculateDocumentVariableAsync.addHandler(
    calculateDocumentVariableAsyncHandler,
  );
  richEdit.document.fields.updateAllFields(function () {
    richEdit.events.calculateDocumentVariableAsync.removeHandler(
      calculateDocumentVariableAsyncHandler,
    );
    richEdit.selection.setSelection(0);
    richEdit.endUpdate();
    richEdit.loadingPanel.hide();

    // splitting into two updates solves the following issue: https://sentry-dev.prismacloud.cc/organizations/prismacloud/issues/8025/?project=15&query=is%3Aunresolved
    // (Cannot read property 'selected' of undefined)
    richEdit.beginUpdate();
    richEdit.loadingPanel.show();
    mergeTablesFromDocumentVariables(richEdit);
    richEdit.endUpdate();
    richEdit.loadingPanel.hide();
  });
}

function postProcessEvaluationResult(richEdit: RichEdit, result: any): any {
  if (result?.type === 'table') {
    const value = result as TableValue;
    return createTableWithCells(richEdit, value.cells);
  }
  if (Array.isArray(result)) {
    if (result.length === 0) {
      return '';
    } else if ((result as any)['__type'] === 'raw-table') {
      const columns = (result as any)['__columns'] as
        | DataBlockTableColumnDto[]
        | null;

      return createTableWithDataAsRows(
        richEdit,
        result,
        columns?.map((x) => x.name),
      );
    } else if (Array.isArray(result[0])) {
      // if variable value is array - we suppose it's a table for repeated data blocks.
      return createTableWithDataAsColumns(richEdit, result);
    } else {
      // reference to a Field of Dictionary type with Multiple values
      return result.join(', ');
    }
  } else {
    return result;
  }
}

type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  mergeFields: MergeFieldDto[];
  patientId: number;
  caseId: number | undefined;
  visitId: number | undefined;
  dataBlockId: number | undefined;
};

export const ShowCodesAndValuesSwitcher: React.FC<Props> = (props) => {
  const { richEdit, mergeFields } = props;
  const i18n = useTranslation();
  const queryClient = useQueryClient();
  const forms = useGlobalForms();

  useEffect(function AddButtonsToRibbon() {
    const richEditCurrent = richEdit.current;

    richEditCurrent.updateRibbon((ribbon) => {
      if (!ribbon) return;
      const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
      // filePanel.insertItem(
      //   new RibbonButtonItem(showFieldCodesButtonId, 'Field Codes', {
      //     icon: 'dxre-icon-ShowAllFieldCodes',
      //     showText: true,
      //   }),
      // );
      filePanel.insertItem(
        new DXR.RibbonButtonItem(
          showFieldValuesButtonId,
          i18n.t('documents.refresh_field_values_button'),
          {
            icon: 'dxre-icon-ShowAllFieldResults',
            showText: true,
          },
        ),
      );
    });

    return () => {
      if (richEdit.current?.isDisposed === false) {
        richEditCurrent.updateRibbon((ribbon) => {
          if (!ribbon) return;
          const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
          // filePanel.removeItem(showFieldCodesButtonId);
          filePanel.removeItem(showFieldValuesButtonId);
        });
      }
    };
  }, []);

  const showCodesAndValuesHandler = useEventCallback(
    async (
      sender: RichEdit | undefined,
      args: CustomCommandExecutedEventArgs | undefined,
    ) => {
      if (!sender || !args) {
        return;
      }

      if (args.commandName === showFieldCodesButtonId) {
        sender.document.fields.showAllFieldCodes();
        replaceNonExistingFields(sender, mergeFields);
      } else if (args.commandName === showFieldValuesButtonId) {
        await updateFieldsAsync(i18n, richEdit.current, queryClient, forms, {
          patientId: props.patientId,
          caseId: props.caseId ?? null,
          visitId: props.visitId ?? null,
          dataBlockId: props.dataBlockId ?? null,
        });
      }
    },
  );

  useEffect(
    function AddHandlerForButtons() {
      richEdit.current.events.customCommandExecuted.addHandler(
        showCodesAndValuesHandler,
      );
      return () => {
        richEdit.current.events.customCommandExecuted.removeHandler(
          showCodesAndValuesHandler,
        );
      };
    },
    [showCodesAndValuesHandler],
  );

  return null;
};
